import { useState, useMemo, useEffect } from 'react';

/**
 * Custom hook: manages all filter state and exports filterParams for server-side queries.
 * No longer depends on loaded nodes — filtering happens on the server.
 */
export default function useGraphFilters() {
    const [showFilters, setShowFilters] = useState(true);
    const [selectedRelationshipTypes, setSelectedRelationshipTypes] = useState(new Set());
    const [selectedSchemas, setSelectedSchemas] = useState(new Set());
    const [selectedApps, setSelectedApps] = useState(new Set());
    const [graphSearch, setGraphSearch] = useState('');
    // THE TYPED TERM AND THE QUERIED TERM ARE NOT THE SAME THING. `graphSearch`
    // is what the box shows and must update on every keystroke; this is what the
    // SERVER is asked for, and it settles first. Without it every character sent
    // TWO requests — the node page and the facets both key off filterParams —
    // so a nine-letter word cost eighteen round trips and the table spent the
    // whole word in its loading state. 250ms is under the threshold where the
    // pause reads as lag, and above a fast typist's inter-key interval.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(graphSearch), 250);
        return () => clearTimeout(t);
    }, [graphSearch]);
    const [valueJsonField, setValueJsonField] = useState('');
    const [valueJsonValue, setValueJsonValue] = useState('');
    const [valueJsonMin, setValueJsonMin] = useState('');
    const [valueJsonMax, setValueJsonMax] = useState('');
    const [valueJsonMatchMode, setValueJsonMatchMode] = useState('contains');
    const [selectedStrata, setSelectedStrata] = useState(new Set());
    const [dateField, setDateField] = useState('created_at'); // 'created_at' | 'updated_at'
    const [dateAfter, setDateAfter] = useState('');
    const [dateBefore, setDateBefore] = useState('');

    // Clear field filter when not exactly one schema selected
    useEffect(() => {
        if (selectedSchemas.size !== 1) {
            setValueJsonField('');
            setValueJsonValue('');
            setValueJsonMin('');
            setValueJsonMax('');
            setValueJsonMatchMode('contains');
        }
    }, [selectedSchemas]);

    // Build API-ready params from filter state
    const filterParams = useMemo(() => {
        const p = {};
        if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
        if (selectedSchemas.size > 0) p.schema_ids = [...selectedSchemas].join(',');
        if (selectedRelationshipTypes.size > 0) p.relationship_types = [...selectedRelationshipTypes].join(',');
        if (selectedApps.size > 0) p.app_names = [...selectedApps].join(',');
        if (selectedStrata.size > 0) p.stratum = [...selectedStrata].join(',');
        // value_json: only contains/exact modes supported server-side
        if (valueJsonField && selectedSchemas.size === 1) {
            const schemaId = [...selectedSchemas][0];
            if (schemaId && schemaId !== '__none__') {
                p.schema_id = schemaId;
                p.value_json_field = valueJsonField;
                if (valueJsonValue) {
                    p.value_json_value = valueJsonValue;
                    if (valueJsonMatchMode && valueJsonMatchMode !== 'contains') p.value_json_match = valueJsonMatchMode;
                }
            }
        }
        if (dateAfter || dateBefore) {
            p.date_field = dateField;
            if (dateAfter) p.date_after = dateAfter;
            if (dateBefore) p.date_before = dateBefore;
        }
        return p;
    }, [debouncedSearch, selectedSchemas, selectedRelationshipTypes, selectedApps, selectedStrata, valueJsonField, valueJsonValue, valueJsonMatchMode, dateField, dateAfter, dateBefore]);

    const _toggle = (setter) => (value) => {
        setter(prev => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });
    };

    const toggleRelationshipType = _toggle(setSelectedRelationshipTypes);
    const toggleSchema = _toggle(setSelectedSchemas);
    const toggleApp = _toggle(setSelectedApps);
    const toggleStratum = _toggle(setSelectedStrata);

    const deselectAllRelTypes = () => setSelectedRelationshipTypes(new Set());
    const deselectAllSchemas = () => setSelectedSchemas(new Set());
    const deselectAllApps = () => setSelectedApps(new Set());
    const deselectAllStrata = () => setSelectedStrata(new Set());

    return {
        showFilters, setShowFilters,
        selectedRelationshipTypes, setSelectedRelationshipTypes,
        selectedSchemas, setSelectedSchemas,
        selectedApps, setSelectedApps,
        selectedStrata, setSelectedStrata,
        graphSearch, setGraphSearch,
        valueJsonField, setValueJsonField,
        valueJsonValue, setValueJsonValue,
        valueJsonMin, setValueJsonMin,
        valueJsonMax, setValueJsonMax,
        valueJsonMatchMode, setValueJsonMatchMode,
        dateField, setDateField,
        dateAfter, setDateAfter,
        dateBefore, setDateBefore,
        filterParams,
        toggleRelationshipType, toggleSchema, toggleApp, toggleStratum,
        deselectAllRelTypes, deselectAllSchemas, deselectAllApps, deselectAllStrata,
    };
}
